import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Users, Trophy, FileText, Vote, AlertTriangle, MapPin, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid } from "recharts";

export default function Analytics() {
    const [overview, setOverview] = useState(null);
    const [provinces, setProvinces] = useState([]);
    const [demo, setDemo] = useState(null);
    const [timeseries, setTimeseries] = useState([]);

    useEffect(() => {
        api.get("/analytics/overview").then((r) => setOverview(r.data));
        api.get("/analytics/provinces").then((r) => setProvinces(r.data.provinces || []));
        api.get("/analytics/demographics").then((r) => setDemo(r.data));
        api.get("/analytics/timeseries?days=7").then((r) => setTimeseries(r.data.timeseries || []));
    }, []);

    const COLORS = ["#06B6D4", "#EF4444", "#FACC15", "#A855F7", "#10B981"];

    return (
        <main className="pt-24 pb-12 min-h-screen">
            <div className="container-x">
                <div className="mb-8 fade-up">
                    <div className="text-xs uppercase tracking-widest text-[var(--gold-deep)] mb-2">Phòng điều hành</div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold">Phân tích thống kê thời gian thực</h1>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                    <KPI icon={<Users />} label="Thí sinh" value={overview?.total_users ?? "—"} />
                    <KPI icon={<Trophy />} label="Cuộc thi" value={overview?.total_contests ?? "—"} />
                    <KPI icon={<FileText />} label="Bài thi" value={overview?.total_submissions ?? "—"} />
                    <KPI icon={<Vote />} label="Lượt vote" value={overview?.total_votes ?? "—"} />
                    <KPI icon={<AlertTriangle />} label="Vote nghi ngờ" value={overview?.suspicious_votes ?? "—"} alert />
                </div>

                <div className="grid lg:grid-cols-3 gap-6 mb-6">
                    {/* Timeseries */}
                    <div className="card p-6 lg:col-span-2">
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp className="w-5 h-5 text-[var(--gold-deep)]" />
                            <h3 className="font-heading font-semibold">Bài thi nộp 7 ngày qua</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={timeseries}>
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis dataKey="day" stroke="#64748B" fontSize={11} />
                                <YAxis stroke="#64748B" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#1E1B4B", border: "1px solid rgba(6,182,212,0.4)", borderRadius: "0.5rem", color: "#F8FAFC" }} />
                                <Line type="monotone" dataKey="count" stroke="#06B6D4" strokeWidth={3} dot={{ fill: "#06B6D4", r: 4 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Gender */}
                    <div className="card p-6">
                        <h3 className="font-heading font-semibold mb-4">Cơ cấu giới tính</h3>
                        {demo?.genders?.length ? (
                            <ResponsiveContainer width="100%" height={220}>
                                <PieChart>
                                    <Pie data={demo.genders.map((g) => ({ ...g, name: { male: "Nam", female: "Nữ", other: "Khác" }[g.gender] || g.gender }))}
                                        dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={80} fill="#06B6D4" label>
                                        {demo.genders.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ background: "#1E1B4B", border: "1px solid rgba(6,182,212,0.4)" }} />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <div className="text-center text-slate-500 py-12 text-sm">Chưa có dữ liệu</div>}
                    </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Provinces */}
                    <div className="card p-6">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin className="w-5 h-5 text-[var(--gold-deep)]" />
                            <h3 className="font-heading font-semibold">Phân bố theo tỉnh thành (Top 10)</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={320}>
                            <BarChart data={provinces.slice(0, 10)} layout="vertical">
                                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                <XAxis type="number" stroke="#64748B" fontSize={11} />
                                <YAxis type="category" dataKey="province" stroke="#64748B" fontSize={11} width={110} />
                                <Tooltip contentStyle={{ background: "#1E1B4B", border: "1px solid rgba(6,182,212,0.4)" }} />
                                <Bar dataKey="count" fill="#06B6D4" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Age groups */}
                    <div className="card p-6">
                        <h3 className="font-heading font-semibold mb-4">Cơ cấu độ tuổi</h3>
                        {demo?.age_groups?.length ? (
                            <ResponsiveContainer width="100%" height={320}>
                                <BarChart data={demo.age_groups}>
                                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                                    <XAxis dataKey="group" stroke="#64748B" fontSize={11} />
                                    <YAxis stroke="#64748B" fontSize={11} />
                                    <Tooltip contentStyle={{ background: "#1E1B4B", border: "1px solid rgba(6,182,212,0.4)" }} />
                                    <Bar dataKey="count" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <div className="text-center text-slate-500 py-12 text-sm">Chưa có dữ liệu</div>}
                    </div>
                </div>
            </div>
        </main>
    );
}

function KPI({ icon, label, value, alert }) {
    return (
        <div className="card p-5">
            <div className={`flex items-center gap-2 ${alert ? "text-red-400" : "text-[var(--gold-deep)]"} mb-2 [&_svg]:w-4 [&_svg]:h-4`}>
                {icon}
                <span className="text-xs uppercase tracking-widest">{label}</span>
            </div>
            <div className="text-3xl font-heading font-bold">{value}</div>
        </div>
    );
}
